import React, { useState } from 'react'
import { motion } from 'framer-motion'

import {
  ReactComponent as ProblemSvg,
} from '../../../assets/images/medilearn/whatwebuild.svg'

import {
  ReactComponent as SolutionSvg,
} from '../../../assets/images/medilearn/solution.svg'

const SolutionWrapper = ({ className }: { className?: string }) => {
  const [isInView, setIsInView] = useState(false)

  return (
    <motion.div
      onViewportEnter={() => setIsInView(true)}
      onViewportLeave={() => setIsInView(false)}
      viewport={{ once: false, amount: 0.2 }}
      className={`${className} ${
        isInView ? 'animate-solution' : ''
      }`}
    >
      <SolutionSvg className="h-auto w-full" />
    </motion.div>
  )
}

const ProblemWrapper = ({ className }: { className?: string }) => {
  const [isInView, setIsInView] = useState(false)

  return (
    <motion.div
      onViewportEnter={() => setIsInView(true)}
      onViewportLeave={() => setIsInView(false)}
      viewport={{ once: false, amount: 0.2 }}
      className={`${className} ${
        isInView ? 'animate-solution' : ''
      }`}
    >
      <ProblemSvg className="h-auto w-full" />
    </motion.div>
  )
}

const sections = [
  {
    title: 'THE SOLUTION',
    para:
      'A conversational AI-driven platform that simplifies medical learning through:',

    points: [
      'Guided interactions',
      'Structured categories',
      'Intelligent symptom analysis',
    ],

    reverse: false,
  },

  {
    title: 'WHAT WE BUILT',
    para:
      'A seamless mobile experience that blends AI + UX design to deliver:',

    points: [
      'Chat-based medical assistant',
      'Category-driven exploration',
      'Smart prompts for symptom understanding',
      'Clean, calming UI',
    ],

    reverse: true,
  },
]

const ProblemMedilearn = () => {
  return (
    <section>
      <div className="mx-auto flex flex-col">
        {/* TOP CONTENT */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          viewport={{ once: false }}
          className="flex items-center justify-center gap-6"
        >
          <div className="h-3 w-3 rounded-full bg-white" />

          <div className="items-center">
            <h2 className="font-gilroy-extrabold text-2xl text-black md:text-4xl xl:text-5xl 2xl:text-6xl">
              THE PROBLEM
            </h2>
          </div>

          <div className="h-3 w-3 rounded-full bg-white" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, scale: 0.7 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          viewport={{ once: false }}
          className="mx-auto mt-4 px-5 text-center font-gilroy-medium text-lg text-white/60 md:max-w-[85%] lg:mt-8 lg:text-2xl xl:max-w-[75%] xl:text-3xl 2xl:text-4xl 2xl:leading-12"
        >
          Healthcare information is often fragmented, overly complex,
          and difficult for non-experts to interpret. Users struggle
          to identify symptoms accurately or navigate reliable medical
          knowledge without feeling overwhelmed or misinformed.
        </motion.p>

        {sections.map((section) => (
          <div
            key={section.title}
            className={`mx-[5%] grid items-center md:mt-5 md:grid-cols-2 md:gap-10 lg:gap-15 xl:mt-0 xl:gap-20 ${
              section.reverse
                ? 'md:[&>*:first-child]:order-2'
                : ''
            }`}
          >
            {/* IMAGE */}
            <motion.div
              initial={{
                opacity: 0,
                x: section.reverse ? 120 : -120,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              transition={{
                duration: 0.9,
                ease: 'easeOut',
              }}
              viewport={{ once: false }}
              className="flex justify-center"
            >
              {section.title === 'THE SOLUTION' ? (
                <SolutionWrapper className="w-full max-w-[700px]" />
              ) : (
                <ProblemWrapper className="w-full max-w-[700px]" />
              )}
            </motion.div>

            {/* CONTENT */}
            <motion.div
              initial={{
                opacity: 0,
                x: section.reverse ? -120 : 120,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              transition={{
                duration: 0.9,
                ease: 'easeOut',
              }}
              viewport={{ once: false }}
            >
              {/* HEADING */}
              <motion.div
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.7,
                  ease: 'easeOut',
                }}
                viewport={{ once: false }}
                className="flex items-center gap-5"
              >
                <h2 className="font-gilroy-extrabold text-xl text-black lg:text-4xl xl:text-5xl 2xl:text-6xl">
                  {section.title}
                </h2>

                <div className="h-3 w-3 rounded-full bg-white" />
              </motion.div>

              {/* PARA */}
              <motion.p
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.8,
                  delay: 0.2,
                  ease: 'easeOut',
                }}
                viewport={{ once: false }}
                className="max-w-[900px] font-gilroy-medium text-base leading-relaxed text-white lg:mt-3 lg:text-2xl xl:mt-5 2xl:text-4xl"
              >
                {section.para}
              </motion.p>

              {/* POINTS */}
              <div className="mt-5 flex flex-col gap-2 md:gap-5 xl:mt-10">
                {section.points.map((point, index) => (
                  <motion.div
                    key={point}
                    initial={{
                      opacity: 0,
                      y: 40,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.15,
                    }}
                    viewport={{ once: false }}
                    className="w-fit rounded-full  py-2 pl-8 pr-15 lg:py-4"
                    style={{
                      borderImage:
                        'linear-gradient(90deg, rgba(255,255,255,.35) 0%, rgba(255,255,255,.35) 75%, rgba(255,255,255,0) 100%) 1',

                      background:
                        'linear-gradient(90deg, rgba(255,255,255,.13) 0%, rgba(255,255,255,.13) 75%, rgba(255,255,255,0) 100%)',

                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-1.5 w-1.5 rounded-full bg-white lg:h-2.5 lg:w-2.5" />

                      <p className="font-gilroy-bold text-sm text-white md:text-base lg:text-xl xl:text-2xl">
                        {point}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default ProblemMedilearn